import { useEffect } from "react";
import { useForm } from "react-hook-form";
import styles from "./profileform.module.css";

import {
  Input,
  Button,
  SubHeadingTitle,
} from "../../../../../components/shared";

import { Link } from "react-router-dom";
import Swal from "sweetalert2";

import {
  useUserProfile,
  useAddUserProfile,
  useUpdateUserProfile,
} from "../../../../../queries/useUserProfile";
import { validateEmail, validateMobile, validateName } from "../../../../../utils/Validations";

const ProfileForm = () => {
  const { data: profileRes, isLoading } = useUserProfile();

  const addMutation = useAddUserProfile();
  const updateMutation = useUpdateUserProfile();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      mobile: "",
      gender: "",
      subscribe: true,
    },
  });

  const profileData = profileRes?.data;

  const isUpdateMode = !!profileData?.id;

  useEffect(() => {
    if (profileData) {
      setValue("name", profileData.username || "");
      setValue("email", profileData.email || "");
      setValue("mobile", profileData.phone_number || "");
      setValue("gender", profileData.gender || "");
      setValue("subscribe", profileData.subscribe ?? true);
    }
  }, [profileData, setValue]);

  const onSubmit = async (formData) => {
    try {
      const payload = new FormData();

      payload.append("username", formData.name);
      payload.append("email", formData.email);
      payload.append("phone_number", formData.mobile);
      payload.append("gender", formData.gender);

      if (!isUpdateMode) {
        await addMutation.mutateAsync(payload);

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Profile added successfully",
          confirmButtonColor: "#0b6b50",
        });
      } else {
        await updateMutation.mutateAsync(payload);

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Profile updated successfully",
          confirmButtonColor: "#156ca1",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Profile save failed",
        confirmButtonColor: "#d33",
      });
    }
  };

  if (isLoading) {
    return <p>Loading profile...</p>;
  }

  return (
    <form
      className={styles.container}
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* =========================
          ROW 1
          NAME + MOBILE
      ========================= */}
      <div className={styles.row}>
        <Input
          label="Name"
          name="name"
          placeholder="Name"
          register={register}
        validation={{
  validate: validateName,
}}
          error={errors?.name?.message}
        />

        <Input
          name="mobile"
          label="Mobile Number"
          readOnly={isUpdateMode}
          placeholder="xxxxxxxxxx"
          register={register}
         validation={{
  validate: validateMobile,
}}
          error={errors?.mobile?.message}
        />
      </div>

      {/* =========================
          ROW 2
          EMAIL + GENDER
      ========================= */}
      <div className={styles.row}>
        <Input
          label="Email"
          name="email"
          readOnly={isUpdateMode}
          placeholder="Enter email"
          register={register}
        validation={{
  validate: validateEmail,
}}
          error={errors?.email?.message}
        />

        <GenderSelector
          selectedGender={watch("gender")}
          onSelect={(gender) => setValue("gender", gender)}
        />
      </div>

      {/* =========================
          NEWSLETTER
      ========================= */}
      <div className={styles.checkboxContainer}>
        <input
          type="checkbox"
          className={styles.subscribe}
          {...register("subscribe")}
        />

        <label>
          Keep up with our latest news and events. Subscribe to our newsletter.
        </label>
      </div>

      {/* =========================
          UPDATE BUTTON
      ========================= */}
      <Button
        title={isUpdateMode ? "Update Profile" : "Add Profile"}
        type="submit"
        style={styles.updateBtn}
        disabled={
          addMutation.isPending || updateMutation.isPending
        }
      />

      {/* =========================
          DELETE ACCOUNT
      ========================= */}
      <p className={styles.deleteText}>
        Do you want to delete your account permanently?
        <Link className={styles.link} to="#">
          Click here
        </Link>
      </p>
    </form>
  );
};


/* =====================================================
   GENDER SELECTOR
===================================================== */

const GenderSelector = ({ selectedGender, onSelect }) => {
  const genders = ["male", "female", "other"];

  return (
    <div className={styles.genderContainer}>
      <SubHeadingTitle
        style={styles.label}
        subTitle="Gender (optional)"
      />

      <div className={styles.buttonGroup}>
        {genders.map((gender) => (
          <Button
            key={gender}
            title={
              gender.charAt(0).toUpperCase() + gender.slice(1)
            }
            type="button"
            onHandler={() => onSelect(gender)}
            style={`${styles.genderBtn} ${
              selectedGender === gender
                ? styles.active
                : ""
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default ProfileForm;